import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ArrowLeft, Mail, Lock, Check, AlertCircle } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import { getBaseUrl } from '@/utils/api';

const API_URL = getBaseUrl();

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  // Request reset state
  const [email, setEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  
  // Reset password state (when token is present)
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetComplete, setResetComplete] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Request password reset
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setRequestSent(true);
      } else {
        throw new Error(data.detail || 'Failed to send reset email');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset password with token
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setResetComplete(true);
      } else {
        throw new Error(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // If we have a token, show reset form
  if (token) {
    return (
      <div className="min-h-screen bg-ivory-mist flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="font-display text-4xl font-bold tracking-tight text-obsidian-night mb-2">
              GNTL STRNGTH
            </h1>
          </div>

          <Card className="bg-white border-burnt-terra/20">
            <CardHeader className="pb-4">
              <CardTitle className="font-display text-obsidian-night text-center">
                {resetComplete ? 'Password Reset' : 'Set New Password'}
              </CardTitle>
              <CardDescription className="text-shadow-pine font-body text-center">
                {resetComplete 
                  ? 'Your password has been updated successfully.'
                  : 'Enter your new password below.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {resetComplete ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-shadow-pine/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-6 h-6 text-shadow-pine" />
                  </div>
                  <Button
                    onClick={() => navigate('/login')}
                    className="bg-burnt-terra hover:bg-burnt-terra/90 text-white"
                  >
                    Go to Login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                      <Input
                        id="new-password"
                        type="password"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                      <Input
                        id="confirm-password"
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-red-600 text-sm">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-burnt-terra hover:bg-burnt-terra/90 text-white"
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
        <BrandMark />
      </div>
    );
  }

  // Request reset form
  return (
    <div className="min-h-screen bg-ivory-mist flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl font-bold tracking-tight text-obsidian-night mb-2">
            GNTL STRNGTH
          </h1>
        </div>

        <Card className="bg-white border-burnt-terra/20">
          <CardHeader className="pb-4">
            <CardTitle className="font-display text-obsidian-night text-center">
              {requestSent ? 'Check Your Email' : 'Forgot Password'}
            </CardTitle>
            <CardDescription className="text-shadow-pine font-body text-center">
              {requestSent 
                ? 'If an account exists, we sent a reset link.'
                : 'Enter your email to receive a reset link.'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {requestSent ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-shadow-pine/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-shadow-pine" />
                </div>
                <p className="text-sm text-shadow-pine/70 mb-4">
                  Check your inbox and follow the link to reset your password.
                </p>
                <p className="text-xs text-shadow-pine/50 mb-4">
                  Didn&apos;t receive an email? Check your spam folder or try again.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setRequestSent(false);
                    setEmail('');
                  }}
                >
                  Try Another Email
                </Button>
              </div>
            ) : (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-burnt-terra hover:bg-burnt-terra/90 text-white"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="text-sm text-burnt-terra hover:underline flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
      <BrandMark />
    </div>
  );
};

export default ForgotPasswordPage;
